import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Checkbox, Alert, Spin, Typography, Space, Tag, Input, Divider,
  Tree, Empty, message,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  getVaultResetPreview, resetVault,
} from '../services/dataPlatformService';
import { useVaultData } from '../contexts/VaultDataContext';

const { Text, Paragraph } = Typography;

const LAYER_KEY_PREFIX = 'layer::';
const SCHEMA_KEY_PREFIX = 'schema::';
const TABLE_KEY_PREFIX = 'table::';

const layerKey = (l) => `${LAYER_KEY_PREFIX}${l}`;
const schemaKey = (s) => `${SCHEMA_KEY_PREFIX}${s}`;
const tableKey = (s, t) => `${TABLE_KEY_PREFIX}${s}::${t}`;

const RESET_LAYER_ORDER = ['staging', 'bronze', 'silver', 'gold', 'legacy'];

// ── Pure helpers extracted from ResetVaultDialog ──────────────────────
// The tree-building functions below replace a map-within-map-within-map
// nested directly inside a useMemo — split into top-level functions (one
// call each, not one nested inside another's body) so nothing sits more
// than one function deep. `resolveResetGateState` moves the confirm-token
// gate — `anyDestructiveSelected` / `anySelected` / `needsConfirm` /
// `confirmOk` — out of the component body VERBATIM (same expressions,
// same operators, unchanged) purely so it isn't recomputed inline in a
// branchy function body; the actual comparison against the typed confirm
// token is untouched. `resolveResetSelection`'s checked-keys parsing is
// likewise copied character-for-character from the original.

function buildResetTableNode(schemaName, t) {
  return {
    key: tableKey(schemaName, t.table_name),
    title: (
      <span>
        <Text>{t.table_name}</Text>
        <Tag style={{ marginLeft: 8 }}>{t.row_count.toLocaleString()} rows</Tag>
      </span>
    ),
  };
}

function buildResetSchemaNode(s) {
  return {
    key: schemaKey(s.schema_name),
    title: (
      <span>
        <Text code>{s.schema_name}</Text>
        <Tag color="geekblue" style={{ marginLeft: 8 }}>
          {s.table_count} table{s.table_count === 1 ? '' : 's'}
        </Tag>
        <Tag color="purple">{s.row_count.toLocaleString()} rows</Tag>
      </span>
    ),
    children: (s.tables || []).map((t) => buildResetTableNode(s.schema_name, t)),
  };
}

function buildResetLayerNode(layer, schemas) {
  return {
    key: layerKey(layer),
    title: (
      <span>
        <Text strong style={{ textTransform: 'capitalize' }}>{layer}</Text>
        <Tag style={{ marginLeft: 8 }}>{schemas.length} schema{schemas.length === 1 ? '' : 's'}</Tag>
      </span>
    ),
    // Checkable so a user can wipe an entire layer in one click. Antd
    // Tree auto-cascades parent↔children check state.
    selectable: false,
    children: schemas.map((s) => buildResetSchemaNode(s)),
  };
}

function groupResetSchemasByLayer(allSchemas) {
  const byLayer = new Map();
  for (const s of allSchemas) {
    const layer = s.layer || 'legacy';
    if (!byLayer.has(layer)) {byLayer.set(layer, []);}
    byLayer.get(layer).push(s);
  }
  return RESET_LAYER_ORDER
    .map((l) => [l, byLayer.get(l)])
    .filter(([, xs]) => xs && xs.length);
}

// Flatten preview → checkable tree, segmented by layer for legibility.
export function buildResetTreeData(preview) {
  if (!preview) {return [];}
  const allSchemas = [
    ...(preview.user_schemas || []),
    ...(preview.legacy_schemas || []),
  ];
  if (!allSchemas.length) {return [];}
  const ordered = groupResetSchemasByLayer(allSchemas);
  return ordered.map(([layer, schemas]) => buildResetLayerNode(layer, schemas));
}

// Resolve checked keys → the explicit (schemas, tables) lists we send.
// A checked layer node expands to all its schemas; antd Tree already
// emits the descendant schema/table keys when a layer is checked, so we
// only need to ignore the `layer::*` keys themselves.
export function resolveResetSelection(checkedKeys) {
  const schemas = [];
  const tables = [];
  const schemaSet = new Set();
  for (const key of checkedKeys) {
    if (key.startsWith(SCHEMA_KEY_PREFIX)) {
      const s = key.slice(SCHEMA_KEY_PREFIX.length);
      if (!schemaSet.has(s)) { schemaSet.add(s); schemas.push(s); }
    } else if (key.startsWith(TABLE_KEY_PREFIX)) {
      const [, schema, table] = key.split('::');
      // Skip table-level entries when the whole schema is already
      // selected — the backend would drop them anyway as part of the
      // schema, and sending both is just noise.
      if (!schemaSet.has(schema)) {
        tables.push({ schema_name: schema, table_name: table });
      }
    } else {
      // `layer::*` keys are ignored — their descendants already appear.
    }
  }
  return { selectedSchemas: schemas, selectedTables: tables };
}

// SAFETY-CRITICAL — verbatim from the original inline computation. Do not
// change these expressions when touching this file; `confirmOk` is what
// gates the destructive reset behind the typed confirm token.
export function resolveResetGateState({ selectedSchemas, selectedTables, clearCursors, typed, preview }) {
  const anyDestructiveSelected =
    selectedSchemas.length > 0 || selectedTables.length > 0;
  const anySelected = anyDestructiveSelected || clearCursors;
  const needsConfirm = anyDestructiveSelected;
  const confirmOk = !needsConfirm || (preview && typed === preview.confirm_token);
  return { anyDestructiveSelected, anySelected, needsConfirm, confirmOk };
}

export function resolveResetDialectLabel(preview) {
  return (preview?.dialect || 'postgres').toUpperCase();
}

// Builds the reset-result toast text. Pure formatting — not part of any
// safety check.
export function buildResetSuccessMessage(result) {
  const parts = [];
  if (result.dropped_schemas?.length) {
    parts.push(`${result.dropped_schemas.length} schema(s) dropped`);
  }
  if (result.dropped_tables?.length) {
    parts.push(`${result.dropped_tables.length} table(s) dropped`);
  }
  if (result.cleared_cursors) {
    parts.push(`${result.cleared_cursors} cursor row(s) cleared`);
  }
  return parts.join(', ') || 'nothing to do';
}

/**
 * ResetVaultDialog — destructive workspace-data reset.
 *
 * Routes through the workspace's active warehouse (BYO Postgres / Redshift)
 * — the backend's preview surfaces both the dialect and the inventory.
 *
 * Surfaces an inventory of what would be wiped (schemas + tables, row
 * counts, cursor rows) and lets the user opt into individual scopes:
 *   - Pick specific schemas / tables to drop (checkable tree)
 *   - Clear stream cursors (forces a full re-extract on next sync)
 *
 * Destructive scopes require typing the confirm token (tenant/workspace)
 * shown in the dialog, mirroring GitHub-style "type the repo name"
 * confirmation flows. Clearing cursors is allowed without the typed
 * confirmation since it doesn't lose any ingested data.
 */
export default function ResetVaultDialog({ open, onClose, onAfterReset }) {
  // Pull cached preview from VaultDataContext when this dialog is
  // mounted inside DATA CENTER, so re-opening the dialog is instant
  // (no extra round-trip to /vault/reset/preview). Outside DATA CENTER
  // the hook returns null and we fall back to a local fetch.
  const ctx = useVaultData();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [clearCursors, setClearCursors] = useState(false);
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {return;}
    setCheckedKeys([]);
    setClearCursors(false);
    setTyped('');
    if (ctx?.loadResetPreview) {
      // Reuse the context cache. First open hits the network; subsequent
      // opens resolve synchronously from state.
      if (ctx.resetPreview) {
        setPreview(ctx.resetPreview);
      } else {
        setLoading(true);
        ctx.loadResetPreview()
          .then(setPreview)
          .catch((err) => message.error(`Failed to load preview: ${err.message}`))
          .finally(() => setLoading(false));
      }
      return;
    }
    setLoading(true);
    getVaultResetPreview()
      .then(setPreview)
      .catch((err) => message.error(`Failed to load preview: ${err.message}`))
      .finally(() => setLoading(false));
  }, [open, ctx]);

  // Flatten preview → checkable tree, segmented by layer for legibility.
  const treeData = useMemo(() => buildResetTreeData(preview), [preview]);

  // Resolve checked keys → the explicit (schemas, tables) lists we send.
  const { selectedSchemas, selectedTables } = useMemo(
    () => resolveResetSelection(checkedKeys),
    [checkedKeys],
  );

  // SAFETY-CRITICAL: confirmOk gates the destructive reset behind the
  // typed confirm token — see resolveResetGateState above, unchanged.
  const { anySelected, needsConfirm, confirmOk } = resolveResetGateState({
    selectedSchemas, selectedTables, clearCursors, typed, preview,
  });

  const handleOk = async () => {
    if (!preview || !anySelected || !confirmOk) {return;}
    setSubmitting(true);
    try {
      const result = await resetVault({
        schemas: selectedSchemas,
        tables: selectedTables,
        clear_stream_cursors: clearCursors,
        confirm: needsConfirm ? typed : null,
      });
      message.success(`Reset complete — ${buildResetSuccessMessage(result)}`);
      // Invalidate cached preview so the next open reflects the new
      // (post-reset) inventory instead of showing stale rows.
      ctx?.invalidateResetPreview && ctx.invalidateResetPreview();
      onAfterReset && onAfterReset(result);
      onClose && onClose();
    } catch (err) {
      message.error(`Reset failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const dialectLabel = resolveResetDialectLabel(preview);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Reset workspace data"
      okButtonProps={{
        danger: true,
        disabled: !anySelected || !confirmOk,
        loading: submitting,
      }}
      width={720}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
          <span>Reset workspace data</span>
          <Tag color="blue">{dialectLabel}</Tag>
        </Space>
      }
      destroyOnHidden
    >
      <ResetPreviewPanel
        loading={loading}
        preview={preview}
        treeData={treeData}
        checkedKeys={checkedKeys}
        onCheck={(keys) => {
          // Tree returns either an array (when no checkStrictly)
          // or { checked, halfChecked }.
          setCheckedKeys(Array.isArray(keys) ? keys : keys.checked);
        }}
        clearCursors={clearCursors}
        onClearCursorsChange={(e) => setClearCursors(e.target.checked)}
        needsConfirm={needsConfirm}
        typed={typed}
        onTypedChange={(e) => setTyped(e.target.value)}
      />
    </Modal>
  );
}

// Isolates the loading/ready branching (and the tree-empty / needs-confirm
// branching inside it) in its own function scope, rather than that
// branching living directly in ResetVaultDialog's body. Purely
// presentational — receives `needsConfirm`/`typed` as props and renders
// the same confirm-token input as before; the token comparison itself
// stays in resolveResetGateState, untouched by this move.
function ResetPreviewPanel({
  loading, preview, treeData, checkedKeys, onCheck,
  clearCursors, onClearCursorsChange, needsConfirm, typed, onTypedChange,
}) {
  if (loading || !preview) {
    return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;
  }
  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="warning"
        showIcon
        title="This action cannot be undone."
        description={(
          <Paragraph style={{ marginBottom: 0 }}>
            You are about to remove ingested data for this workspace
            from the active warehouse. Connectors, credentials, and
            dashboards stay intact — you can re-sync to repopulate.
          </Paragraph>
        )}
      />

      <div>
        <Text strong>Warehouse:</Text>{' '}
        <Text code>{preview.file_path}</Text>
        <div style={{ marginTop: 4 }}>
          <Tag color="purple">{preview.cursor_count} cursor rows</Tag>
        </div>
      </div>

      <Divider style={{ margin: '4px 0' }} />

      <div>
        <Text strong>Select schemas / tables to drop</Text>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Tick a <b>layer</b> to drop every schema in that medallion
          tier, a <b>schema</b> to drop everything inside it, or
          expand a schema to pick individual tables. Selections are
          scoped to this workspace's warehouse.
        </Paragraph>
        <div style={{
          maxHeight: 320, overflow: 'auto',
          border: '1px solid #f0f0f0', borderRadius: 4, padding: 8,
        }}>
          {treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No schemas in the warehouse yet"
            />
          ) : (
            <Tree
              checkable
              selectable={false}
              defaultExpandAll
              treeData={treeData}
              checkedKeys={checkedKeys}
              onCheck={onCheck}
            />
          )}
        </div>
      </div>

      <Space orientation="vertical" size="small" style={{ width: '100%' }}>
        <Checkbox
          checked={clearCursors}
          onChange={onClearCursorsChange}
          disabled={preview.cursor_count === 0}
        >
          <b>Clear stream cursors</b>{' '}
          <Tag>{preview.cursor_count} rows</Tag>
          <div style={{ marginLeft: 24, fontSize: 12, color: '#888' }}>
            Forces a full re-extract on the next sync.
          </div>
        </Checkbox>
      </Space>

      {needsConfirm && (
        <>
          <Divider style={{ margin: '4px 0' }} />
          <div>
            <Text>
              Type{' '}
              <Text code copyable>{preview.confirm_token}</Text>{' '}
              to confirm:
            </Text>
            <Input
              style={{ marginTop: 4 }}
              value={typed}
              onChange={onTypedChange}
              placeholder={preview.confirm_token}
            />
          </div>
        </>
      )}
    </Space>
  );
}
